import { useState } from "react";
import { useLocation } from "wouter";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "../ui/Card";
import ErrorToast from "../ui/ErrorToast";
import SuccessToast from "../ui/SucessToast";
import { api } from "../../lib/api";

export default function ResetPassword() {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false); 
    const [showConfirmPassword, setShowConfirmPassword] = useState(false); 
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [, setLocation] = useLocation();
    const [location] = useLocation();

    const urlParams = new URLSearchParams(location.split("?")[1]);
    const email = urlParams.get("email");
    const token = location.split("/").pop().split("?")[0];

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError(null);
        setSuccess(null);

        if (!password || !confirmPassword)
            return setError("Please fill all fields");

        if (password !== confirmPassword)
            return setError("Passwords do not match");

        setIsPending(true);

        try {
            const res = await fetch(api.auth.resetPassword.path(token), {
                method: api.auth.resetPassword.method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password, email }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            setSuccess("Password updated successfully! Redirecting to login...");

            setTimeout(() => {
                setLocation("/login");
            }, 1000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsPending(false);
        }
    };

    return (
        <div
            className="min-h-screen w-full flex items-center justify-center relative overflow-hidden"
            style={{
                backgroundImage:
                    "linear-gradient(to top, #48c6ef 0%, #6f86d6 100%)",
            }}
        >
            <Card className="w-full max-w-md border shadow-2xl bg-white/80 backdrop-blur-xl rounded-2xl">
                <CardHeader className="space-y-3 text-center pb-4">
                    <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                        <Lock className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold">
                        Reset Password
                    </CardTitle>
                    <CardDescription>
                        Set a new password for <b>{email}</b>
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2 relative">
                            <label className="text-sm font-medium ml-1">
                                New Password
                            </label>
                            <Input
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter new password"
                                className="h-12 rounded-xl pr-10"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <div
                                className="absolute right-3 top-[37px] cursor-pointer"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <EyeOff size={16}  className="text-gray-500" />
                                ) : (
                                    <Eye size={16} className="text-gray-500" />
                                )}
                            </div>
                        </div>

                        <div className="space-y-2 relative">
                            <label className="text-sm font-medium ml-1">
                                Confirm Password
                            </label>
                            <Input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm new password"
                                className="h-12 rounded-xl pr-10"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <div
                                className="absolute right-3 top-[37px] cursor-pointer"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (
                                    <EyeOff size={16} className="text-gray-500" />
                                ) : (
                                    <Eye size={16} className="text-gray-500" />
                                )}
                            </div>
                        </div>

                        <Button
                            type="submit"
                            variant="success"
                            className="w-full h-12 rounded-xl text-base  bg-blue-500 text-white"
                            disabled={isPending}
                        >
                            {isPending ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                "Reset Password"
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {error && (
                <ErrorToast
                    message={error}
                    duration={4000}
                    onClose={() => setError(null)}
                />
            )}

            {success && (
                <SuccessToast
                    message={success}
                    duration={4000}
                    onClose={() => setSuccess(null)}
                />
            )}
        </div>
    );
}
